import { useState, useRef } from 'react'
import cx from 'classnames'
import * as yup from 'yup'
import { Formik, FormikProps, Form } from 'formik'
import { useQueryClient } from 'react-query'

import { authFromResponse } from 'utils'
import { reportError } from 'utils/error'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import Shared from '../../Shared.module.scss'
import Styles from './ResetPassword.module.scss'
import PasswordFields from '../PasswordFields/PasswordFields'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import useLoginOrSignUpOrSignUp from '../../hooks/useLoginOrSignUp'
import { RegistrationType } from 'screens/LoginOrSignup/utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import RightPanelContent from '../RightPanelContent/RightPanelContent'
import { useHistory, useLocation } from 'react-router-dom'
import { GhostButton } from 'components/Buttons/GhostButton'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { Button } from 'blaze-ui/Button'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'

type Props = {
  token: string
  email: string
  transitionPage: (page: string) => void
  workspaceInviteToken: string
  redirectTo: string
}

type FormValueType = {
  password: string
}

const initialValues = {
  password: '',
}

const ERROR_FIELD_LABELS: { [key: string]: string | number } = {
  password: 'Password',
}

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required('isRequired')
    .min(8, 'isNotLongEnough')
    .matches(/\d+/, 'noNumber')
    .matches(/[A-Z]+/, 'noCapital'),
})

const ResetPassword = (props: Props) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { isTabletPortrait } = useResponsiveDesign()
  const { token, email, transitionPage, workspaceInviteToken, redirectTo } = props
  const [passwordResetError, setPasswordResetError] = useState(false)
  const [passwordSubmitted, setPasswordSubmitted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [submittedWithErrors, setSubmittedWithErrors] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isBlaze = useIsBlaze()

  const { onLoginOrSignUpSuccess: onLoginSuccess } = useLoginOrSignUpOrSignUp()

  const formikRef = useRef<FormikProps<FormValueType>>(null)

  const handlePasswordPressEnter = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter' || e.code === 'Tab') {
      setPasswordSubmitted(true)
    }
  }

  const errorMessage = (errors: { firstName?: string; lastName?: string; password?: string }) => {
    const errorFields = Object.keys(errors)
      .map((key) => ERROR_FIELD_LABELS[key])
      .join(', ')

    return `Please check the ${errorFields || ''} ${Object.keys(errors).length > 1 ? ' fields' : ' field'} above`
  }

  const resetPassword = async (password: string) => {
    const body = { reset_password_token: token, password }
    setIsProcessing(true)

    const res = await client.resetUserPassword(body)

    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      setPasswordResetError(true)
      setIsProcessing(false)
      reportError(res.data, { metaData: 'Error resetting password' })
      return
    }

    const loginRes = await client.login(email, password)
    if (loginRes.type !== SUCCESS_RESPONSE_TYPE) {
      setIsProcessing(false)
      if (loginRes.data.error) showToastMessage(loginRes.data.error, 'error')
      return
    }

    const auth = authFromResponse(loginRes.data.data, loginRes.headers)

    const nextPage = await onLoginSuccess(auth, RegistrationType.EmailAndPassword, redirectTo, workspaceInviteToken)

    queryClient.invalidateQueries('featureFlags')

    setIsProcessing(false)

    if (nextPage) transitionPage(nextPage)
  }

  const goBack = () => {
    const queryParams = new URLSearchParams(location.search)
    queryParams.delete('reset_password_token')

    history.replace({ search: queryParams.toString() })
    transitionPage('GO_BACK')
  }

  if (isOnboardingRedesign) {
    return (
      <div data-testid="resetPassword" className={cx(Styles.content)}>
        <div className={Styles.title}>
          <Heading level={1}>Reset password</Heading>
          <Text color="var(--dark-40)">Resetting password for {email}</Text>
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={(data) => {
            resetPassword(data.password)
          }}
          validationSchema={validationSchema}
          validateOnChange
          validateOnMount
        >
          {({ values, errors, handleChange }) => {
            const { password } = values

            const handlePasswordSubmit = () => {
              Object.keys(errors).length ? setSubmittedWithErrors(true) : setSubmittedWithErrors(false)
            }

            return (
              <Form className={cx(Shared.form, Styles.form)}>
                <PasswordFields
                  password={password}
                  passwordLabel="New Password"
                  errors={errors}
                  handleChange={handleChange}
                  handleSubmitPassword={handlePasswordPressEnter}
                  passwordSubmitted={passwordSubmitted}
                  submittedWithErrors={submittedWithErrors}
                  isProcessing={isProcessing}
                  passwordPlaceholder="Enter password"
                />
                {!!Object.keys(errors).length && passwordSubmitted && (
                  <FormErrorMessage>{errorMessage(errors)}</FormErrorMessage>
                )}
                <div className={Styles.buttonsContainer}>
                  <Button variant="secondary" size="xl" onPress={goBack}>
                    Back
                  </Button>
                  <Button type="submit" onPress={handlePasswordSubmit} isDisabled={isProcessing} size="xl">
                    Update Password & Login
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    )
  }

  return (
    <div data-testid="resetPassword" className={cx(Shared.content, { [Shared.isBlaze]: isBlaze })}>
      <div className={Shared.leftPanel}>
        <div className={Shared.header}>
          <GhostButton
            className={cx(Shared.backButton, { [Shared.isBlaze]: isBlaze })}
            aria-label="Back"
            onClick={goBack}
          >
            <LeftArrowIcon />
          </GhostButton>
        </div>

        <div className={Shared.largerLeftContent}>
          <div className={cx(Shared.title, Styles.title)}>Reset password</div>
          <div className={Shared.subTitle}>
            <div>{'Reset the password for'}</div>
            <div className={Styles.emailBoldText}>{email}</div>
          </div>
          {passwordResetError && (
            <div className={Shared.errorContainer}>
              <p className={Shared.creationError}>
                There was a problem with your request.{' '}
                <button
                  type="button"
                  onClick={() => {
                    setPasswordResetError(false)
                    formikRef.current?.resetForm()
                  }}
                >
                  Try again.
                </button>
              </p>
            </div>
          )}
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={(data) => {
              resetPassword(data.password)
            }}
            validationSchema={validationSchema}
            validateOnChange
            validateOnMount
          >
            {({ values, errors, handleChange }) => {
              const { password } = values

              const handlePasswordSubmit = () => {
                Object.keys(errors).length ? setSubmittedWithErrors(true) : setSubmittedWithErrors(false)
              }

              return (
                <Form className={cx(Shared.form, Styles.form)}>
                  <PasswordFields
                    password={password}
                    passwordLabel="New Password"
                    errors={errors}
                    handleChange={handleChange}
                    handleSubmitPassword={handlePasswordPressEnter}
                    passwordSubmitted={passwordSubmitted}
                    submittedWithErrors={submittedWithErrors}
                    isProcessing={isProcessing}
                    passwordPlaceholder="Enter password"
                  />
                  {!!Object.keys(errors).length && passwordSubmitted && (
                    <FormErrorMessage>{errorMessage(errors)}</FormErrorMessage>
                  )}
                  <div className={Styles.submitButton}>
                    <Button
                      type="submit"
                      onPress={handlePasswordSubmit}
                      isDisabled={isProcessing}
                      variant="primary"
                      size="lg"
                    >
                      <span>Update password and login</span>
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </div>
      {!isTabletPortrait && <RightPanelContent screen="continueWithEmailOrGmail" />}
    </div>
  )
}

export default ResetPassword
